import React from "react";
import ReactDOM from "react-dom/client";
import {createBrowserRouter, Navigate, RouterProvider} from "react-router-dom";
import "./index.css";
import App from "./App";
import English from "./lang/en.json";
import German from "./lang/de.json";
import primeEnglish from "./lang/primereact/en.json";
import primeGerman from "./lang/primereact/de.json";
import appSettingsLocal from "./appsettings.LOCAL.json";
import ScreenSizeProvider from "./utility/ScreenSize";
import {PortalStyling} from "@spg/component_library/portal";
import {IntlProvider} from "react-intl";
import KeycloakProvider from "./utility/KeycloakProvider";
import {EnrichedSnackbarProvider} from "./utility/SnackbarProvider";
import Invitations from "./pages/Invitations/Invitations";
import Account from "./pages/Account/Account";
import Home from "./pages/Home/Home";
import Imprint from "./pages/unprotectedPages/Imprint";
import {ModalProvider, SnackbarProvider} from "@spg/component_library";
import Terms from "./pages/unprotectedPages/Terms/Terms";
import DisplayBooking from "./pages/DisplayBooking/DisplayBooking";
import {
  PrimeReactProvider,
  addLocale,
  locale as primeLocale,
} from "primereact/api";
import "primereact/resources/themes/md-light-indigo/theme.css";
import {getLocaleFromAvailable} from "./logic/Locale";
import {AppSettings} from "./logic/APITypes";
import AuthErrorPage from "./pages/unprotectedPages/AuthErrorPage";
import AppSettingsProvider from "./utility/AppSettingsProvider";

const locale = navigator.language;
let lang = English as Record<string, string>;
addLocale("de", primeGerman.de);
addLocale("en", primeEnglish.en);
primeLocale("en");
if (getLocaleFromAvailable() === "de") {
  lang = German as Record<string, string>;
  primeLocale("de");
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

const onSettingsLoaded = (appSettings: AppSettings) => {
  if (appSettings.texts.city.length > 0) {
    lang["home.titles.page"] += " " + appSettings.texts.city;
  }
  appSettings.texts.imprint.forEach((part, i) => {
    lang["imprint.titles.section" + i] = part.title;
    lang["imprint.paragraphs.section" + i] = part.paragraph;
  });
  const childPaths = [
    {
      path: "/",
      element: <Home />,
    },
    {
      path: "/account",
      element: <Account />,
    },
    {
      path: "/invitations",
      element: <Invitations />,
    },
  ];

  if (appSettings.digitalStand.enabled)
    childPaths.push({
      path: "/digital-stand",
      element: <DisplayBooking />,
    });

  const router = createBrowserRouter([
    {
      path: "/",
      element: <App />,
      children: childPaths,
    },
    {
      path: "/imprint",
      element: <Imprint />,
    },
    {
      path: "/terms",
      element: (
        <ModalProvider>
          <Terms />
        </ModalProvider>
      ),
    },
    {
      path: "*",
      element: <Navigate to="/" />,
    },
  ]);

  root.render(
    <AppSettingsProvider appSettings={appSettings}>
      <KeycloakProvider>
        <React.StrictMode>
          <IntlProvider locale={locale} messages={lang}>
            <PrimeReactProvider>
              <ScreenSizeProvider>
                <SnackbarProvider>
                  <EnrichedSnackbarProvider>
                    <>
                      <PortalStyling />
                      <RouterProvider router={router} />
                    </>
                  </EnrichedSnackbarProvider>
                </SnackbarProvider>
              </ScreenSizeProvider>
            </PrimeReactProvider>
          </IntlProvider>
        </React.StrictMode>
      </KeycloakProvider>
    </AppSettingsProvider>,
  );
};

const onSettingsError = () => {
  root.render(
    <IntlProvider locale={locale} messages={lang}>
      <PortalStyling />
      <AuthErrorPage reason={"noAppSettings"} afterTimeout={() => null} />
    </IntlProvider>,
  );
};

if (process.env.NODE_ENV === "development") onSettingsLoaded(appSettingsLocal);
else
  fetch("/appsettings.json").then(
    (res) =>
      res
        .json()
        .then((settings) => onSettingsLoaded(settings), onSettingsError),
    onSettingsError,
  );
